import humps from 'humps';

import { axios } from '@/apis/library';
import enumTypes, {
  PLATFORM_MEDIA_TYPES,
  PLATFORM_MEDIA_TYPES_MAP,
  SNAPCHAT_MEDIA_TYPES,
} from '@/app/library/constants';
import { constants, SOURCE_TO_METRIC_PROP, typeEnum, UPLOAD_STATUS } from '@/config';
import { formatTime } from '@/utils/formatters';

const MAX_CROPPER_IMAGE_DIMENSION = 11000 * 11000;

export function getMediaTypeFromDetail(mediaDetail) {
  switch (mediaDetail?.mediaType) {
    case 'VIDEO':
      return 'video';
    case 'IMAGE':
      return 'photo';
    default:
      return 'media';
  }
}

// Following two functions are identical should remove
export function getImageDataFromMedia(media) {
  // Uploaded to scheduler popup (MediaV2)
  if (media.fullMediaObject) {
    return media.fullMediaObject.sizes.originalConverted || media.fullMediaObject.sizes.original;
  }
  // everything else MediaV2
  return media.sizes.originalConverted || media.sizes.original;
}

export function getVideoDataFromMedia(media) {
  // Uploaded to scheduler popup (MediaV2)
  if (media?.fullMediaObject) {
    return media.fullMediaObject.sizes.originalConverted || media.fullMediaObject.sizes.original;
  }
  // everything else MediaV2
  return media?.sizes?.originalConverted || media?.sizes?.original || null;
}

export function mediaIsUploading(media) {
  return (
    Boolean(media?.uploadStatus) &&
    media?.uploadStatus !== UPLOAD_STATUS.SUCCESS &&
    media?.uploadStatus !== UPLOAD_STATUS.PROCESSING
  );
}

export async function getVideoDuration(media, brandId) {
  let videoDuration = null;
  // Only videos selected from the global popup will have a value for duration.
  // Otherwise we need to call library to get that value.
  if (media.duration === undefined || media.duration == null) {
    const res = await axios.get(`/brands/${brandId}/media/${media.id}`);
    videoDuration = res.data.duration;
  } else {
    videoDuration = media.duration;
  }
  return videoDuration;
}

export function mediaSizeIsCroppable(media) {
  if (media.type === 'IMAGE') {
    const mediaObject = media.fullMediaObject || media;
    const imageWidth = mediaObject.sizes.originalConverted
      ? mediaObject.sizes.originalConverted.width
      : mediaObject.sizes.original.width;
    const imageHeight = mediaObject.sizes.originalConverted
      ? mediaObject.sizes.originalConverted.height
      : mediaObject.sizes.original.height;

    // Larger images could cause cropper crashes
    return imageWidth * imageHeight <= MAX_CROPPER_IMAGE_DIMENSION;
  }
  // TODO: if future media size validation is decided upon, it can exist here. For now return
  // true if the media type is video.
  return media.type === 'VIDEO';
}

export function getMediaSourceKey(media) {
  return SOURCE_TO_METRIC_PROP[media?.source] || media?.source?.toLowerCase();
}

function getMediaSourceData(media) {
  const sourceKey = getMediaSourceKey(media);
  return media?.[sourceKey];
}

export function isMediaVideo(media) {
  const isVideoMedia = media?.type === 'VIDEO' || media?.mediaType === 'VIDEO';
  const isPinterestVideo = media?.pinterest?.isVideo;
  const isTikTokVideo = media?.tiktok && media.tiktok.mediaType?.toUpperCase() !== 'PHOTO';
  const isYouTubeVideo = media?.youtube;
  const isFacebookVideo = media?.facebook?.type?.toUpperCase() === 'VIDEO';
  const isSnapchatVideo = false;
  return isVideoMedia || isPinterestVideo || isTikTokVideo || isYouTubeVideo || isFacebookVideo;
}

export function isMediaText(media) {
  const isFacebookText = media?.facebook?.type === 'status';
  const isLinkedInText = media?.linkedin?.type === 'TEXT';
  const isThreadsText = media?.threads?.type === 'TEXT_POST';
  const isTwitterText = media?.twitter?.type === 'text';
  return isFacebookText || isLinkedInText || isThreadsText || isTwitterText;
}

export function isMediaDocument(media) {
  const isLinkedInDocument = media?.linkedin?.type === 'DOCUMENT';
  return isLinkedInDocument;
}

export function isMediaAudio(media) {
  const isThreadsAudio = media?.threads?.type === 'AUDIO';
  return isThreadsAudio;
}

export function isMediaLink(media) {
  return (
    enumTypes.FACEBOOK_LINK_LIST.includes(media?.source) ||
    enumTypes.TWITTER_LINK_LIST.includes(media?.source)
  );
}

/**
 * Checks if media is an exportable image (excludes videos, carousels, text, documents, audio, links)
 * @param {Object} media
 * @returns {boolean}
 */
export function isMediaImage(media) {
  const mediaType = media?.type || media?.mediaType || media?.media_type;

  if (
    isMediaVideo(media) ||
    isMediaText(media) ||
    isMediaDocument(media) ||
    isMediaAudio(media) ||
    isMediaLink(media)
  ) {
    return false;
  }

  if (mediaType?.toUpperCase() === 'CAROUSEL') {
    return false;
  }

  if (mediaType?.toUpperCase() === 'IMAGE' && !media?.is_video && !media?.isVideo) {
    return true;
  }

  return false;
}

export function isMediaReel(media) {
  return media?.instagram?.postType?.toLowerCase() === 'reel';
}

export function isMediaPromoted(media) {
  return Boolean(
    media.instagram?.isBoosted ||
      media.facebook?.isBoosted ||
      media.twitter?.isPromoted ||
      media.instagram?.is_boosted ||
      media.facebook?.is_boosted ||
      media.twitter?.is_promoted ||
      media.tiktok?.distributionType === 'PROMOTED',
  );
}

function isMediaCarousel(media) {
  if (media?.instagram) {
    return media?.instagram?.isCarousel;
  }

  const sourceData = getMediaSourceData(media);
  if (sourceData?.twitter?.additionalMediaUrls) {
    return sourceData.twitter.additionalMediaUrls !== '';
  }
  return sourceData?.type?.toLowerCase() === 'carousel';
}

function getMediaImageUrl(media) {
  const isVideo = isMediaVideo(media);
  const isPinterestVideo = media?.pinterest?.isVideo;
  let mediaImages;

  if (isPinterestVideo) {
    mediaImages = media?.image?.sizes;
  } else {
    mediaImages = isVideo ? media?.video?.thumbnails : media?.image?.sizes;
  }
  return humps.camelizeKeys(mediaImages)?.smallSquare?.url;
}

function getMediaPostUrl(media) {
  const source = media?.source;
  const sourceData = media?.sourceData;
  if (source === 'PINTEREST') {
    return sourceData?.link;
  }
  if (source === 'TWITTER') {
    return sourceData?.permalinkUrl;
  }
  if (source === 'TIKTOK') {
    return sourceData?.shareUrl;
  }
  return sourceData?.url;
}

export function getMediaVideoDuration(media) {
  return (
    media?.video?.duration ||
    media?.tiktok?.duration ||
    media?.youtube?.duration ||
    media?.videoDuration ||
    undefined
  );
}

export function resolvePlatformMediaType(media) {
  const camelMedia = humps.camelizeKeys(media);
  const channel = SOURCE_TO_METRIC_PROP[media?.source] || media?.source?.toLowerCase();

  if (!channel) return null;

  const channelData = camelMedia[humps.camelize(channel)] ?? {};
  const data = { ...camelMedia, ...channelData };

  if (data.source?.includes('_LINK')) {
    return PLATFORM_MEDIA_TYPES.LINK;
  }

  const type = (data.mediaType || data.postType || data.type)?.toUpperCase() ?? null;

  if (channel === constants.TIKTOK.toLowerCase()) {
    if (data?.tiktok?.mediaType === 'PHOTO') {
      return PLATFORM_MEDIA_TYPES.IMAGE;
    }
    return PLATFORM_MEDIA_TYPES.VIDEO;
  }

  if (
    type === typeEnum.MULTI_IMAGE ||
    data[channel]?.additionalMediaUrls?.length > 0 ||
    data[channel]?.carouselMediaList?.length > 0
  ) {
    return PLATFORM_MEDIA_TYPES.CAROUSEL;
  }

  if (channel === constants.INSTAGRAM_STORY_FRAME.toLowerCase()) {
    return type === 'VIDEO' ? PLATFORM_MEDIA_TYPES.STORY_FRAME : PLATFORM_MEDIA_TYPES.STORY;
  }

  if (channel === constants.THREADS.toLowerCase() && data.threads?.type === 'TEXT_POST') {
    return PLATFORM_MEDIA_TYPES.TEXT;
  }

  if (channel === constants.SNAPCHAT.toLowerCase()) {
    return SNAPCHAT_MEDIA_TYPES?.[data.snapchat?.assetType] ?? type;
  }

  return PLATFORM_MEDIA_TYPES_MAP[type] || type;
}

export function formatMediaVideoDuration(media) {
  // default duration string placeholder is '--:--'
  let durationString = '--:--';
  const isPinterestVideo = media?.pinterest?.isVideo ?? media?.pinterest?.is_video;
  const videoDuration = getMediaVideoDuration(media);
  if (isPinterestVideo) {
    // since pinterest doesn't give us the video file itself or duration data,
    // simply leave the duration string out
    durationString = '';
  } else if (videoDuration) {
    durationString = formatTime(videoDuration);
  }
  return durationString;
}

export function normalizeMedia(media) {
  const isVideo = isMediaVideo(media);
  const isCarousel = isMediaCarousel(media);
  const isLink = isMediaLink(media);
  const isReel = isMediaReel(media);
  const imageUrl = getMediaImageUrl(media);
  const postUrl = getMediaPostUrl(media);
  const videoDuration = getMediaVideoDuration(media);
  return {
    id: media?.id,
    isVideo,
    isCarousel,
    isLink,
    isReel,
    imageUrl,
    postUrl,
    videoDuration,
  };
}

export function checkTweetMediaIsOriginal(tweet) {
  let itemInsight;
  if (tweet?.twitter) {
    itemInsight = tweet?.twitter;
  } else {
    itemInsight = tweet?.insights?.twitter || tweet?.insights;
  }
  return !(
    itemInsight?.inReplyToUserId ||
    itemInsight?.inReplyToStatusId ||
    itemInsight?.retweetedStatusId ||
    itemInsight?.quotedStatusId
  );
}

export function getMetricValue(media, metricAliasMap) {
  const sourceData = getMediaSourceData(media);
  const channel = media.source === 'INSTAGRAM_STORY' ? media.source : media.source.split('_')[0];
  const metric = metricAliasMap[channel];
  return sourceData?.[metric];
}
